import React, { useState, useRef } from "react";
import Text from "./text";

const SafetyMarginSlider = ({ safetyMargin, setSafetyMargin }) => {
  const [showSafetyInfo, setShowSafetyInfo] = useState(false);
  const infoBtnRef = useRef(null);
  const { WhatsNew: text } = Text;

  const sliderColor =
    safetyMargin <= 10
      ? "bg-green-500"
      : safetyMargin <= 20
      ? "bg-yellow-500"
      : "bg-red-500";

  // Close popover when clicking outside
  React.useEffect(() => {
    if (!showSafetyInfo) return;
    const handler = (e) => {
      if (infoBtnRef.current && !infoBtnRef.current.contains(e.target)) {
        setShowSafetyInfo(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showSafetyInfo]);

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 relative">
      <div className="grid md:grid-cols-4 gap-4">
        {/* Safety Margin Label + Info Button */}
        <div className="md:col-span-4">
          <div className="flex items-center space-x-2 relative">
            <h3 className="text-gray-800 font-medium">Safety Margin:</h3>
            <span
              className={`px-2 py-0.5 rounded font-medium ${
                safetyMargin <= 10
                  ? "bg-green-100 text-green-800"
                  : safetyMargin <= 20
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {safetyMargin}%
            </span>
            <div ref={infoBtnRef} className="relative">
              <button
                type="button"
                className="text-blue-500 hover:text-blue-700 focus:outline-none"
                onClick={() => setShowSafetyInfo((v) => !v)}
                aria-label="More information about safety margin"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {showSafetyInfo && (
                <div
                  className={`
                    absolute z-50 w-80 bg-white border border-blue-200 rounded-lg shadow-lg p-4 text-sm text-blue-900
                    ${
                      window.innerWidth < 768
                        ? "left-1/2 -translate-x-1/2 mt-4"
                        : "left-0 mt-2"
                    }
                  `}
                  style={window.innerWidth < 768 ? { right: "auto" } : {}}
                >
                  <div className="flex items-center mb-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 flex-shrink-0 text-blue-600 mr-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <h4 className="font-semibold text-blue-800">
                      {text.SafetyMargin.HowItWorks.Title}
                    </h4>
                  </div>
                  <div className="space-y-3 text-blue-800">
                    <div className="flex">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 border-2 border-green-500 mr-2 mt-0.5"></div>
                      <p>
                        <strong className="text-green-700">
                          {text.SafetyMargin.HowItWorks.Safe.Title}:
                        </strong>{" "}
                        {text.SafetyMargin.HowItWorks.Safe.Description}
                      </p>
                    </div>
                    <div className="flex">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-yellow-100 border-2 border-yellow-500 mr-2 mt-0.5"></div>
                      <p>
                        <strong className="text-yellow-700">
                          {text.SafetyMargin.HowItWorks.Moderate.Title}:
                        </strong>{" "}
                        {text.SafetyMargin.HowItWorks.Moderate.Description}
                      </p>
                    </div>
                    <div className="flex">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 border-2 border-red-500 mr-2 mt-0.5"></div>
                      <p>
                        <strong className="text-red-700">
                          {text.SafetyMargin.HowItWorks.Ambitious.Title}:
                        </strong>{" "}
                        {text.SafetyMargin.HowItWorks.Ambitious.Description}
                      </p>
                    </div>
                    <div className="pt-1">
                      <p>
                        <span className="font-semibold">Current Setting:</span>{" "}
                        Showing colleges within {safetyMargin}% of your rank{" "}
                        {safetyMargin > 0 ? "(including below your rank)" : ""}.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Slider */}
        <div className="md:col-span-4">
          <input
            type="range"
            min="0"
            max="30"
            value={safetyMargin}
            onChange={(e) => setSafetyMargin(parseInt(e.target.value))}
            className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${sliderColor}`}
            style={{
              WebkitAppearance: "none",
              appearance: "none",
            }}
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1 px-1">
            <span>{text.SafetyMargin.HowItWorks.Safe.Title.split(" ")[0]}</span>
            <span>
              {text.SafetyMargin.HowItWorks.Moderate.Title.split(" ")[0]}
            </span>
            <span>
              {text.SafetyMargin.HowItWorks.Ambitious.Title.split(" ")[0]}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafetyMarginSlider;
